/**
 * Roster selectors: grouping for the "who is working" grid and SOC-capability.
 */
import type { BranchConfig, ConfigState, DateKey, DemoState, Discipline, DisciplineGroup, RosterEntry } from "@caredna-marlin/utils/homeHealthDemo/state";
import { TRAIT_OASIS } from "@caredna-marlin/utils/homeHealthDemo/fixtures/config";
import { addDaysToKey, dateKeyRange, toDateKey } from "@caredna-marlin/utils/homeHealthDemo/engine/clock";
import { shiftsOn } from "./clinicianDay";
import { supplyTerritoriesFor, territoryForZip } from "@caredna-marlin/utils/homeHealthDemo/engine/territory";

export { supplyTerritoriesFor };

export type RosterGroupKey = "snAssessing" | "snAssistant" | "therapy" | "socialWorkAides";

export const ROSTER_GROUP_LABELS: Record<RosterGroupKey, string> = {
  snAssessing: "Skilled nursing — assessing (RN)",
  snAssistant: "Skilled nursing — assistant (LPN)",
  therapy: "Therapy",
  socialWorkAides: "Social work & aides",
};

export const ROSTER_GROUP_ORDER: RosterGroupKey[] = ["snAssessing", "snAssistant", "therapy", "socialWorkAides"];

export function rosterGroupFor(entry: RosterEntry): RosterGroupKey {
  switch (entry.discipline) {
    case "RN":
      return "snAssessing";
    case "LPN":
      return "snAssistant";
    case "PT":
    case "PTA":
    case "OT":
    case "COTA":
    case "SLP":
      return "therapy";
    case "MSW":
    case "HHA":
      return "socialWorkAides";
  }
}

/** SN = RN + LPN, PT = PT + PTA, OT = OT + COTA; the rest map to themselves. */
export function disciplineGroupFor(discipline: Discipline): DisciplineGroup {
  switch (discipline) {
    case "RN":
    case "LPN":
      return "SN";
    case "PT":
    case "PTA":
      return "PT";
    case "OT":
    case "COTA":
      return "OT";
    case "SLP":
      return "SLP";
    case "MSW":
      return "MSW";
    case "HHA":
      return "HHA";
  }
}

export const DISCIPLINE_GROUP_ORDER: DisciplineGroup[] = ["SN", "PT", "OT", "SLP", "MSW", "HHA"];

export const DISCIPLINE_GROUP_LABELS: Record<DisciplineGroup, string> = {
  SN: "Skilled nursing",
  PT: "Physical therapy",
  OT: "Occupational therapy",
  SLP: "Speech therapy",
  MSW: "Social work",
  HHA: "Home health aide",
};

export function primaryTerritory(entry: Pick<RosterEntry, "territories" | "homeZip" | "territory">, branch: BranchConfig): string {
  return supplyTerritoriesFor(entry, branch)[0];
}

export function ownsTerritory(entry: Pick<RosterEntry, "territories">, territory: string): boolean {
  return entry.territories.includes(territory);
}

export function rosterEntries(state: Pick<DemoState, "roster" | "sandbox">): RosterEntry[] {
  const removed = new Set(state.sandbox?.removedHandles ?? []);
  const base = Object.values(state.roster).filter((e) => !removed.has(e.handle));
  return [...base, ...(state.sandbox?.addedClinicians ?? [])];
}

export function rosterByGroup(state: Pick<DemoState, "roster" | "sandbox">): { key: RosterGroupKey; label: string; entries: RosterEntry[] }[] {
  const entries = rosterEntries(state);
  return ROSTER_GROUP_ORDER.map((key) => ({
    key,
    label: ROSTER_GROUP_LABELS[key],
    entries: entries.filter((e) => rosterGroupFor(e) === key),
  })).filter((g) => g.entries.length > 0);
}

export function isInRamp(entry: RosterEntry, date: DateKey): boolean {
  return !!entry.rampEndsOn && date <= entry.rampEndsOn;
}

/**
 * SOC-capable = an assessing discipline with the OASIS-competent trait and not in
 * orientation ramp on that day. LPN, PTA, COTA, HHA and MSW never count.
 */
export function isSocCapable(entry: RosterEntry, date: DateKey): boolean {
  return entry.roleKind === "assessing" && entry.traits.includes(TRAIT_OASIS) && !isInRamp(entry, date);
}

/** Case-discipline rule: which disciplines may open a case for a given order set. */
export function socPathDisciplines(orderDisciplines: string[]): ("RN" | "PT" | "OT" | "SLP")[] {
  if (orderDisciplines.includes("RN")) return ["RN"];
  const therapy = orderDisciplines.filter((d) => d === "PT" || d === "OT" || d === "SLP");
  if (therapy.length === 0) return ["RN"];
  const path: ("RN" | "PT" | "OT" | "SLP")[] = [];
  if (therapy.includes("PT")) path.push("PT");
  if (therapy.includes("SLP")) path.push("SLP");
  if (therapy.includes("OT") && (therapy.includes("PT") || therapy.includes("SLP"))) path.push("OT");
  return path;
}

export function rosterEntryForProvider(state: Pick<DemoState, "roster" | "meta">, providerId: number): RosterEntry | undefined {
  const handle = Object.entries(state.meta.bindings.providerIdsByHandle).find(([, id]) => id === providerId)?.[0];
  return handle ? state.roster[handle] : undefined;
}

export function hasTraits(entry: RosterEntry, required: string[]): boolean {
  return required.every((t) => entry.traits.includes(t));
}

export function visitTypeLabel(config: ConfigState, kind: keyof ConfigState["visitTypes"]): string {
  return config.visitTypes[kind].label;
}

// ---------------------------------------------------------------------------
// The per diem roster (5.2)
// ---------------------------------------------------------------------------

/** How far ahead the roster shows declared days. */
export const PER_DIEM_DECLARED_DAYS = 14;

/**
 * A declared day is a real shift inside the horizon and the fixture's declared
 * day beyond it — the same data, and inside the horizon the same rule, that the
 * coverage drawer uses to put a per diem in **External, availability declared**.
 * The drawer only ever asks about a day within a week of today, so the two can
 * never disagree about a day either of them shows.
 */
export function isDeclaredDay(state: Pick<DemoState, "server" | "config" | "meta">, entry: RosterEntry, dateKey: DateKey): boolean {
  const providerId = state.meta.bindings.providerIdsByHandle[entry.handle] ?? entry.providerId;
  const { rangeStart, rangeEnd } = state.server;
  if (rangeStart && rangeEnd && dateKey >= rangeStart && dateKey <= rangeEnd) {
    return shiftsOn(state.server.shifts, providerId, dateKey, state.config.branch.timeZone).length > 0;
  }
  return !!entry.declaredDays?.includes(dateKey);
}

export interface PerDiemRosterRow {
  handle: string;
  displayName: string;
  discipline: Discipline;
  /** Declared days from today, inclusive, over the next fortnight. */
  declaredDays: DateKey[];
  lastWorkedOn?: DateKey;
}

/** The last day this clinician completed a visit inside the horizon, else what the roster carries. */
function lastWorkedFor(state: Pick<DemoState, "server" | "config" | "meta">, entry: RosterEntry): DateKey | undefined {
  const providerId = state.meta.bindings.providerIdsByHandle[entry.handle] ?? entry.providerId;
  if (providerId === undefined) return entry.lastWorkedOn;
  const tz = state.config.branch.timeZone;
  let latest: DateKey | undefined;
  for (const vr of state.server.visitResources) {
    if (vr.providerId !== providerId || vr.eventStatus !== "complete") continue;
    const day = toDateKey(new Date(vr.startIso), tz);
    if (!latest || day > latest) latest = day;
  }
  if (!latest) return entry.lastWorkedOn;
  return entry.lastWorkedOn && entry.lastWorkedOn > latest ? entry.lastWorkedOn : latest;
}

/**
 * The per diem roster (6.1): who is on the list, the days each declared, and
 * when each last worked. No outreach button —
 * bringing someone in is the branch's decision and its channel (B9).
 */
export function perDiemRoster(state: Pick<DemoState, "roster" | "sandbox" | "server" | "config" | "meta">, today: DateKey): PerDiemRosterRow[] {
  const days = dateKeyRange(today, addDaysToKey(today, PER_DIEM_DECLARED_DAYS - 1));
  return rosterEntries(state)
    .filter((e) => e.employment === "perDiem")
    .sort((a, b) => a.displayName.localeCompare(b.displayName))
    .map((entry) => ({
      handle: entry.handle,
      displayName: entry.displayName,
      discipline: entry.discipline,
      declaredDays: days.filter((d) => isDeclaredDay(state, entry, d)),
      lastWorkedOn: lastWorkedFor(state, entry),
    }));
}
